import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Trash2, Plus, Type, AlignLeft, Hash, Calendar, Clock, ToggleLeft, List as ListIcon,
  Link2, Settings2, ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'textarea', label: 'Textarea', icon: AlignLeft },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'datetime', label: 'Date & Time', icon: Clock },
  { type: 'boolean', label: 'Yes/No', icon: ToggleLeft },
  { type: 'select', label: 'Select', icon: ListIcon },
  { type: 'link', label: 'Link', icon: Link2 },
];

// 🎯 نفس القايمة المستخدمة في CustomAppStudio.js لحقول الـ Link من نوع
// core_table - ثابتة عن قصد عشان الأدمن يختار بس من الجداول اللي فعلاً
// معرّفة في /lookups/{type} في main.py، مش يكتب اسم جدول عشوائي يضرب 404
const CORE_TABLES = [
  'locations', 'storerooms', 'assets', 'items', 'tools', 'itemstools', 'companies', 'sla',
  'purchaserequests', 'spareparts', 'roles', 'jobplans', 'failureclasses', 'failurecodes',
  'persons', 'labors', 'meters', 'crafts', 'materialrequest', 'safetyplans', 'inventory',
  'organizations', 'classifications', 'vendors', 'tax_category',
];

const END_OF_TAB = '__end_of_tab__';
const propInputClass = "w-full border-b-2 border-gray-100 focus:border-primary outline-none py-1.5 text-xs font-bold text-gray-700";

const LinkTargetEditor = ({ options, domains, otherModels, onChange, t }) => (
  <div className="space-y-2 bg-surface-panel rounded-md p-3 border border-line">
    <div className="flex gap-1">
      {[
        ['domain', t('customApps.studio.domain', 'Domain')],
        ['core_table', t('customApps.studio.systemTable', 'System Table')],
        ['custom_model', t('customApps.studio.customObject', 'Custom Object')],
      ].map(([lt, lbl]) => (
        <button key={lt} type="button"
          onClick={() => onChange({ ...options, link_type: lt })}
          className={`flex-1 px-1.5 py-1.5 rounded text-[8px] font-black uppercase ${(options?.link_type || 'core_table') === lt ? 'bg-primary text-white' : 'bg-surface-card text-gray-500'}`}
        >{lbl}</button>
      ))}
    </div>
    {options?.link_type === 'domain' && (
      <select className={propInputClass} value={options?.domain_id || ''} onChange={(e) => onChange({ ...options, domain_id: e.target.value })}>
        <option value="">— {t('customApps.studio.selectDomain', 'Select a domain')} —</option>
        {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}{d.description ? ` - ${d.description}` : ''}</option>)}
      </select>
    )}
    {options?.link_type === 'custom_model' && (
      <select className={propInputClass} value={options?.custom_model_slug || ''} onChange={(e) => onChange({ ...options, custom_model_slug: e.target.value })}>
        <option value="">— {otherModels.length === 0 ? t('customApps.studio.noOtherObjects', 'No other objects yet') : t('customApps.studio.selectObject', 'Select an object')} —</option>
        {otherModels.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
      </select>
    )}
    {(!options?.link_type || options?.link_type === 'core_table') && (
      <>
        <select className={propInputClass} value={options?.core_table || ''} onChange={(e) => onChange({ ...options, link_type: 'core_table', core_table: e.target.value })}>
          <option value="">— {t('customApps.studio.selectTable', 'Select a table')} —</option>
          {CORE_TABLES.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase">
          <input type="checkbox" checked={Boolean(options?.filter_by_site)}
            onChange={(e) => onChange({ ...options, filter_by_site: e.target.checked })}
            className="accent-primary" />
          {t('customApps.filterBySite', 'Filter results by current site')}
        </label>
      </>
    )}
  </div>
);

// 🎯 نقطة إدخال حقول كستم جوه شاشة موجودة فعلاً - بتتحط جوه section حقيقي
// (أو نص التاب لو section=null) بدل ما تتحط في تاب منفصل مبتور عن باقي
// الحقول. لو مفيش حقول متعرّفة على الـ anchor ده أصلاً، الكومبوننت مايرندرش
// أي حاجة - الشاشات اللي لسه مالهاش حقول كستم تفضل زي ما هي بالظبط.
const ExtendScreenStudio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { screenKey } = useParams();

  const [screen, setScreen] = useState(null);
  const [fields, setFields] = useState([]);
  const [domains, setDomains] = useState([]);
  const [otherModels, setOtherModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [newField, setNewField] = useState({
    field_type: 'text', tab: '', section: END_OF_TAB, label: '',
    is_required: false, is_readonly: false, show_in_list: true, options: {},
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anchorsRes, fieldsRes, domainsRes, modelsRes] = await Promise.all([
        api.get('/custom-fields/anchors'),
        api.get('/custom-fields', { params: { core_model_name: screenKey } }),
        api.get('/domains', { params: { limit: 200 } }).catch(() => ({ data: [] })),
        api.get('/custom-models', { params: { published_only: false } }).catch(() => ({ data: [] })),
      ]);
      const screenDef = anchorsRes.data?.[screenKey];
      setScreen(screenDef);
      setFields(fieldsRes.data || []);
      setDomains(domainsRes.data?.data || []);
      setOtherModels(modelsRes.data || []);
      const firstTab = Object.keys(screenDef?.tabs || {})[0] || '';
      setNewField((prev) => ({ ...prev, tab: firstTab }));
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this screen'));
    } finally {
      setLoading(false);
    }
  }, [screenKey, t]);

  useEffect(() => { load(); }, [load]);

  const sectionOptions = useMemo(() => screen?.tabs?.[newField.tab] || [], [screen, newField.tab]);

  const handleAdd = async () => {
    const typeInfo = FIELD_TYPES.find((f) => f.type === newField.field_type);
    const label = newField.label.trim() || typeInfo?.label || newField.field_type;
    try {
      const res = await api.post('/custom-fields/extend', {
        core_model_name: screenKey,
        tab: newField.tab,
        section: newField.section,
        field_name: `${newField.field_type}_${fields.length + 1}`,
        label,
        field_type: newField.field_type,
        options: newField.field_type === 'select' || newField.field_type === 'link' ? newField.options : undefined,
        is_required: newField.is_required,
        is_readonly: newField.is_readonly,
        show_in_list: newField.show_in_list,
      });
      setFields((prev) => [...prev, res.data]);
      setNewField((prev) => ({ ...prev, label: '', options: {}, is_required: false, is_readonly: false, show_in_list: true }));
      toast.success(t('customApps.fieldAdded', 'Field added'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('customApps.addFieldFailed', 'Failed to add field'));
    }
  };

  const handleUpdateField = (field, patch) => {
    const updated = { ...field, ...patch };
    setFields((prev) => prev.map((f) => (f.id === field.id ? updated : f)));
    api.put(`/custom-fields/${field.id}`, patch).catch(() => {
      toast.error(t('customApps.saveFailed', 'Failed to save'));
    });
  };

  const handleDelete = async (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    try {
      await api.delete(`/custom-fields/${fieldId}`);
    } catch (err) {
      toast.error(t('customApps.deleteFieldFailed', 'Failed to delete field'));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400">…</div>;
  if (!screen) return <div className="p-6 text-gray-400 text-sm font-bold">{t('customApps.extendScreens.unknownScreen', 'Unknown screen')}</div>;

  return (
    <div className="flex flex-col h-full bg-surface-app overflow-hidden">
      <div className="bg-surface-card p-4 border-b shadow-card flex items-center gap-3">
        <button onClick={() => navigate('/admin/custom-apps/extend')} className="text-gray-400 hover:text-primary transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-primary font-black italic uppercase text-lg tracking-tighter">{screen.label}</h2>
      </div>

      <div className="flex-1 overflow-auto p-6 max-w-3xl mx-auto w-full space-y-6">
        <div className="bg-surface-card border border-line rounded-lg p-4 space-y-3">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('customApps.extendScreens.addField', 'Add a field to this screen')}</span>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              className="border-b-2 border-gray-100 outline-none py-2 text-xs font-bold"
              value={newField.field_type}
              onChange={(e) => setNewField((prev) => ({ ...prev, field_type: e.target.value, options: {} }))}
            >
              {FIELD_TYPES.map((ft) => <option key={ft.type} value={ft.type}>{ft.label}</option>)}
            </select>
            <select
              className="border-b-2 border-gray-100 outline-none py-2 text-xs font-bold"
              value={newField.tab}
              onChange={(e) => setNewField((prev) => ({ ...prev, tab: e.target.value, section: END_OF_TAB }))}
            >
              {Object.keys(screen.tabs || {}).map((tabName) => <option key={tabName} value={tabName}>{tabName}</option>)}
            </select>
            <select
              className="border-b-2 border-gray-100 outline-none py-2 text-xs font-bold"
              value={newField.section}
              onChange={(e) => setNewField((prev) => ({ ...prev, section: e.target.value }))}
            >
              <option value={END_OF_TAB}>{t('customApps.extendScreens.endOfTab', 'End of tab (no section)')}</option>
              {sectionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              className="border-b-2 border-gray-100 focus:border-blue-600 outline-none py-2 text-xs font-bold"
              placeholder={t('customApps.extendScreens.fieldLabel', 'Field label')}
              value={newField.label}
              onChange={(e) => setNewField((prev) => ({ ...prev, label: e.target.value }))}
            />
          </div>

          {newField.field_type === 'select' && (
            <input
              className="w-full border-b-2 border-gray-100 focus:border-blue-600 outline-none py-2 text-xs font-bold"
              placeholder={t('customApps.studio.choices', 'Choices (comma-separated)')}
              value={(newField.options?.choices || []).join(', ')}
              onChange={(e) => setNewField((prev) => ({
                ...prev,
                options: { choices: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) },
              }))}
            />
          )}

          {newField.field_type === 'link' && (
            <LinkTargetEditor
              options={newField.options}
              domains={domains}
              otherModels={otherModels.filter((m) => m.slug)}
              onChange={(options) => setNewField((prev) => ({ ...prev, options }))}
              t={t}
            />
          )}

          <div className="flex flex-wrap items-center gap-4 pt-1">
            <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={newField.is_required}
                onChange={(e) => setNewField((prev) => ({ ...prev, is_required: e.target.checked }))} />
              {t('customApps.studio.required', 'Required')}
            </label>
            <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={newField.is_readonly}
                onChange={(e) => setNewField((prev) => ({ ...prev, is_readonly: e.target.checked }))} />
              {t('customApps.studio.readOnly', 'Read Only')}
            </label>
            <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={newField.show_in_list}
                onChange={(e) => setNewField((prev) => ({ ...prev, show_in_list: e.target.checked }))} />
              {t('customApps.extendScreens.showInList', 'Show in list')}
            </label>
          </div>

          <button
            onClick={handleAdd}
            className="bg-primary text-white px-4 py-2 rounded-control font-black text-[10px] uppercase flex items-center gap-1.5 hover:bg-primary-hover transition-all"
          >
            <Plus size={14} /> {t('common.add', 'Add')}
          </button>
        </div>

        <div className="space-y-3">
          {Object.keys(screen.tabs || {}).map((tabName) => {
            const tabFields = fields.filter((f) => f.tab === tabName);
            if (tabFields.length === 0) return null;
            return (
              <div key={tabName} className="border border-line rounded-lg overflow-hidden">
                <div className="bg-surface-panel px-3 py-2 border-b border-line">
                  <span className="text-[10px] font-black text-ink uppercase tracking-widest">{tabName}</span>
                </div>
                <div className="p-2 space-y-1.5">
                  {tabFields.map((field) => {
                    const isExpanded = expandedId === field.id;
                    return (
                      <div key={field.id} className="rounded hover:bg-surface-panel transition-colors">
                        <div className="flex items-center gap-3 p-2">
                          <span className="text-[9px] font-black text-gray-400 uppercase w-24 shrink-0">{field.section || t('customApps.extendScreens.endOfTab', 'End of tab')}</span>
                          <input
                            className="flex-1 font-bold text-sm outline-none border-b border-transparent focus:border-primary bg-transparent min-w-0"
                            value={field.label}
                            onChange={(e) => handleUpdateField(field, { label: e.target.value })}
                          />
                          <span className="text-[9px] font-black text-gray-300 uppercase shrink-0">{field.field_type}</span>
                          <button onClick={() => setExpandedId(isExpanded ? null : field.id)} className="text-gray-300 hover:text-primary shrink-0">
                            {isExpanded ? <ChevronUp size={14} /> : <Settings2 size={14} />}
                          </button>
                          <button onClick={() => handleDelete(field.id)} className="text-gray-300 hover:text-status-danger shrink-0">
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="px-2 pb-3 pt-1 space-y-2 border-t border-line-subtle mt-1">
                            <div className="flex flex-wrap items-center gap-4">
                              <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
                                <input type="checkbox" className="accent-primary" checked={Boolean(field.is_required)}
                                  onChange={(e) => handleUpdateField(field, { is_required: e.target.checked })} />
                                {t('customApps.studio.required', 'Required')}
                              </label>
                              <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
                                <input type="checkbox" className="accent-primary" checked={Boolean(field.is_readonly)}
                                  onChange={(e) => handleUpdateField(field, { is_readonly: e.target.checked })} />
                                {t('customApps.studio.readOnly', 'Read Only')}
                              </label>
                              <label className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase cursor-pointer">
                                <input type="checkbox" className="accent-primary" checked={Boolean(field.show_in_list)}
                                  onChange={(e) => handleUpdateField(field, { show_in_list: e.target.checked })} />
                                {t('customApps.extendScreens.showInList', 'Show in list')}
                              </label>
                            </div>
                            {field.field_type === 'select' && (
                              <input
                                className="w-full border-b-2 border-gray-100 focus:border-blue-600 outline-none py-1.5 text-xs font-bold"
                                placeholder={t('customApps.studio.choices', 'Choices (comma-separated)')}
                                value={(field.options?.choices || []).join(', ')}
                                onChange={(e) => handleUpdateField(field, {
                                  options: { choices: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) },
                                })}
                              />
                            )}
                            {field.field_type === 'link' && (
                              <LinkTargetEditor
                                options={field.options}
                                domains={domains}
                                otherModels={otherModels.filter((m) => m.slug)}
                                onChange={(options) => handleUpdateField(field, { options })}
                                t={t}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {fields.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-xs font-bold uppercase">
              {t('customApps.extendScreens.none', 'No custom fields on this screen yet')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtendScreenStudio;
